import React from 'react';
import { FaChartArea } from 'react-icons/fa';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import xor from 'lodash/xor';
import { Dropdown, Menu } from 'antd';
import { withRouter } from 'react-router-dom';
import {
  VisualisationWrapper, NoVisualisation, EditDashboardIcon, PanelFilterIcon,
} from './CardSpacer.styles';
import LoadSpinnerOverlay from '../LoadSpinner';
import Visualisation from '../Visualisations';
import { generateFilterTreeObj } from '../Filters';
import { appliedFilterForDashboard } from '../Filters/applyFilter';
import Card from '../InfoCard';

const RENDERER_MAPPINGS = {
  panel: Visualisation,
  card: Card,
};

const requiresFrontendFilter = (filterProcess, globalFilterApplied, prevGlobalFilterApplied) => {
  if (filterProcess === 'frontend') {
    return true;
  }

  // If the filterProcess is any other string (eg. 'generic'), it requires backend filtering.
  if (!Array.isArray(filterProcess)) {
    return false;
  }

  // If any applied filter contains any of the filter processes, a backend filter is required.
  const diff = xor(globalFilterApplied, prevGlobalFilterApplied);
  return !diff.some(filter => filterProcess.some(process => filter.includes(process)));
};

class CardSpacer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.getVisualisation = this.getVisualisation.bind(this);
    this.spinnerShowOrHide = this.spinnerShowOrHide.bind(this);
    this.handleChangeToUpdatePanelFilter = this.handleChangeToUpdatePanelFilter.bind(this);
    this.determineApplyFilter = this.determineApplyFilter.bind(this);
  }

  componentDidMount() {
    this.getVisualisation();
  }

  componentDidUpdate(prevProps) {
    const {
      visualisationData: { data },
      visualisation: { definition, userMeta: { preferredVisSubType, unit } },
      globalFilterApplied,
      match: { params },
      onChangePrefConfig,
      activeDashboard,
    } = this.props;
    const isSameDashboardTab = isEqual(activeDashboard, prevProps.activeDashboard);
    const isSamePanel = isEqual(definition, prevProps.visualisation.definition);
    const isSameURL = isEqual(params, prevProps.match.params);
    const isSameFilter = isEqual(globalFilterApplied, prevProps.globalFilterApplied);
    const isSameUnit = isEqual(unit, prevProps.visualisation.userMeta.unit);
    const filterFromFrontEnd = definition
      && data[definition.dataEndpoint]
      && requiresFrontendFilter(
        data[definition.dataEndpoint].filterProcess,
        globalFilterApplied,
        prevProps.globalFilterApplied,
      );

    if (
      !isSamePanel
      || (!isSameUnit && definition.units.length > 1)
      || !isSameURL
      || (
        !isSameFilter
        && !filterFromFrontEnd
      )
    ) {
      this.getVisualisation(isSamePanel, filterFromFrontEnd, unit, isSameDashboardTab);

      // This changes the preferred visualisation sub-type to the first template
      // if the preferred type is not included in the template list.
      // TODO: Some of the backend visualisation that only has a table for a panel,
      // for some reason it includes an empty line-chart object, which the table icon
      // failed to be highlighted.
      if (definition && !definition.template[preferredVisSubType]) {
        const useNewTypeIfNotAvailable = Object.keys(definition.template)[0];
        onChangePrefConfig(this.getPanelId(), useNewTypeIfNotAvailable || 'table');
      }
    }
  }

  // TODO: This part is a bit messy. Should find a time to make this better when things are
  // less busy.
  getVisualisation(isSamePanel, filterFromFrontEnd, setUnit, isSameDashboardTab) {
    const { api, visualisation, onChangePrefUnit } = this.props;
    const currentVis = visualisation.definition;
    const currentVisUnits = currentVis ? currentVis.units : [];
    // If not the same dashboard, use either saved value or the first unit in list.
    let useUnit = visualisation.userMeta.unit || currentVisUnits[0] || 'None';
    // If is same dashboard, it is usually people swapping units. But this also handles
    // if visualisation changes, the old selected unit would usually break, thus
    // use the first one in the list instead.
    if (isSameDashboardTab) {
      if (currentVisUnits.find(unit => unit === setUnit)) useUnit = setUnit;
      else useUnit = currentVisUnits[0] || 'None';
    } else if (useUnit === 'None' && currentVisUnits.length > 0) {
      // If 'None' is saved and assigned but if the definition has a list of units,
      // please use the first one as 'None' is not supported by backend in this case.
      useUnit = currentVisUnits[0] || 'None';
    }

    if (!currentVis || !currentVis.dataEndpoint) {
      return false;
    }
    currentVis.prevIsNotSame = true;

    // Backend filter is only applied to all cards and certain panel (usually the table-based panel)
    api.getVisualisation(
      currentVis.dataEndpoint,
      useUnit,
      (
        currentVis.allowableBoxes === 'card'
        || !filterFromFrontEnd
      ),
      () => { onChangePrefUnit(this.getPanelId(), useUnit); },
    );

    // If not same panel, make sure reinit the panel filter too.
    if (!isSamePanel) {
      this.handleChangeToUpdatePanelFilter(null);
    }
    return true;
  }

  getPanelId() {
    const { visualisation } = this.props;
    return get(visualisation, 'userMeta.position');
  }

  getAvailableVisualisationOptions() {
    const {
      visualisations, visualisation, onChangeVisualisation, allowableVisualisationType,
    } = this.props;
    const chartPosition = get(visualisation, 'userMeta.position');
    const activeVisualisationId = get(visualisation, 'definition.id');
    const menuItemEventFactory = visName => () => onChangeVisualisation(chartPosition, visName);

    // Filter the visualisation list in menu - based on "panel" / " card".
    const filteredVisualisations = Object.keys(visualisations)
      .filter(key => (
        allowableVisualisationType.includes(visualisations[key].allowableBoxes)
      ))
      .reduce((obj, key) => ({
        ...obj,
        [key]: visualisations[key],
      }), {});

    return (
      <Menu>
        {Object.values(filteredVisualisations).map(vis => (
          <Menu.Item
            key={vis.id}
            onClick={menuItemEventFactory(vis.name)}
          >
            {`${vis.name} ${activeVisualisationId === vis.id ? '(Active)' : ''}`}
          </Menu.Item>
        ))}
      </Menu>
    );
  }

  spinnerShowOrHide() {
    const { visualisationData, visualisation, editing } = this.props;

    if (!visualisation.definition && !editing) {
      return true;
    }

    if (visualisation.definition) {
      if (
        visualisation.definition.prevIsNotSame
        && visualisationData.loadingQueue.length > 0
      ) {
        return true;
      }

      visualisation.definition.prevIsNotSame = false;
    }

    return false;
  }

  // This is merely the panel's filter, the global filter states are maintained
  // in container. Nevertheless, the filtering process is within this file.
  handleChangeToUpdatePanelFilter(changedFilters) {
    const { onChangePanelFilter } = this.props;
    onChangePanelFilter(this.getPanelId(), changedFilters);
  }

  determineApplyFilter(data) {
    const {
      globalFilterApplied, globalFilterDef,
      visualisation: { userMeta: { panelFilterApplied = [] } },
    } = this.props;
    const returnedData = (
      data
      && data.filter
      && Object.keys(data.filter).length > 0
    ) ? appliedFilterForDashboard(
        appliedFilterForDashboard(data, globalFilterApplied, globalFilterDef),
        panelFilterApplied,
        data.filter,
      )
      : appliedFilterForDashboard(data, globalFilterApplied, globalFilterDef);

    return returnedData;
  }

  render() {
    const {
      visualisationData, visualisation, editing, allowableVisualisationType, visualisationActions,
      onChangeVisualisation, onChangePrefConfig, onChangePrefUnit, controls, deltaVisualisations,
      context,
    } = this.props;
    const { definition, userMeta: { panelFilterApplied = [] } } = visualisation;
    const data = definition && visualisationData.data[
      definition.dataEndpoint
    ];
    const hasNoData = data && data.no_data_message;
    const visualisations = this.getAvailableVisualisationOptions();
    const Renderer = definition
      && allowableVisualisationType.includes(definition.allowableBoxes)
      && RENDERER_MAPPINGS[definition.allowableBoxes];
    const filteredData = this.determineApplyFilter(data);
    const spinnerShow = this.spinnerShowOrHide();
    return (
      <VisualisationWrapper
        visible={Renderer || editing}
        m="0"
        p="-1"
        bg="greyscale.6"
      >
        <LoadSpinnerOverlay spinning={spinnerShow} />
        {Renderer && definition && data && !hasNoData && !spinnerShow ? (
          <Renderer
            data={filteredData}
            {
              ...visualisation.definition.associatedVisualisation
              && {
                active: deltaVisualisations[controls] && (
                  deltaVisualisations[controls].toLowerCase()
                  === definition.associatedVisualisation.toLowerCase()
                ),
                onCardClick: () => onChangeVisualisation(
                  controls,
                  definition.associatedVisualisation,
                ),
              }
            }
            prefConfig={visualisation.userMeta.preferredVisSubType}
            onChangePrefConfig={val => onChangePrefConfig(this.getPanelId(), val)}
            onChartClick={e => visualisationActions.selectWeekDay(e)}
            onUnitListClick={({ key }) => onChangePrefUnit(this.getPanelId(), key)}
            context={context}
            unitList={definition.units}
          />
        ) : (
          <NoVisualisation editing={editing} color="greyscale.3" p={2}>
            <FaChartArea />
            <h4>{hasNoData ? 'No Data Available' : 'No Visualisation Selected'}</h4>
          </NoVisualisation>
        )}
        <Dropdown
          overlay={visualisations}
          trigger={['click']}
          overlayClassName="DCRPVisualisationSelection"
        >
          <EditDashboardIcon
            color="greyscale.2"
          />
        </Dropdown>
        {Renderer && definition && !hasNoData && data && data.filter
          && (Object.keys(data.filter).length > 0) && (
          <PanelFilterIcon
            color="greyscale.2"
            hookToState={panelFilterApplied}
            onChange={this.handleChangeToUpdatePanelFilter}
            filters={generateFilterTreeObj(data.filter)}
          />
        )}
      </VisualisationWrapper>
    );
  }
}

export default withRouter(CardSpacer);
